import React from 'react';
import {
  Button,
  Input,
  Message,
  Table,
  Tooltip,
  Whisper,
  useToaster,
} from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import * as partnersApi from '../../api/partners';
import EditPartnerModal from './EditPartnerModal';
import CreatePartnerModal from './CreatePartnerModal';
import UploadImageModal from '../../common/UploadImageModal';
import { API_DOMAIN } from '../../config';
import PageLayout from '../../common/PageLayout';
import ColumnsButton from '../../common/ColumnsButton';
import { useTableColumns } from '../../common/useTableColumns';
import { formatDate, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import TablePagination from '../../common/TablePagination';
import { useFormErrors } from '../../common/useFormErrors';
import { useConfirm } from '../../common/useConfirm';
import { useResourceList } from '../../common/useResourceList';
import { truncatedTextCell } from '../../common/tableCells';

const { Column, HeaderCell, Cell } = Table;
const PARTNER_LONG_DESCRIPTION_PREVIEW_MAX_LENGTH = 250;
const PARTNER_TABLE_IMAGE_SIZE = 72;

function ImageThumb({ src, alt }: { src?: string | null; alt?: string }) {
  const [errored, setErrored] = React.useState(false);
  const showPlaceholder = !src || errored;
  if (!showPlaceholder) {
    return (
      <img
        alt={alt || 'partner'}
        src={src as string}
        onError={() => setErrored(true)}
        style={{
          objectFit: 'contain',
          width: PARTNER_TABLE_IMAGE_SIZE,
          height: PARTNER_TABLE_IMAGE_SIZE,
          borderRadius: 'var(--radius-sm)',
        }}
      />
    );
  }
  return (
    <div
      aria-label="Завантажити зображення"
      title="Натисніть, щоб додати зображення"
      style={{
        width: PARTNER_TABLE_IMAGE_SIZE,
        height: PARTNER_TABLE_IMAGE_SIZE,
        borderRadius: 'var(--radius-sm)',
        border: '1px dashed var(--border)',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--muted)',
        background: 'var(--surface-alt)',
      }}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <circle cx="9" cy="9" r="2" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    </div>
  );
}

const TABLE_COLUMNS = [
  { value: 'name', label: 'Назва', display: true, width: 240, minWidth: 200 },
  {
    value: 'link',
    label: 'Посилання',
    display: true,
    width: 260,
    minWidth: 220,
  },
  {
    value: 'image',
    label: 'Зображення',
    display: true,
    width: 110,
    minWidth: 96,
  },
  {
    value: 'description',
    label: 'Опис',
    display: true,
    width: 420,
    minWidth: 260,
  },
  {
    value: 'descriptionLong',
    label: 'Довгий опис',
    display: true,
    width: 520,
    minWidth: 360,
  },
  {
    value: 'createdAt',
    label: 'Створено',
    display: false,
    width: 140,
    minWidth: 120,
  },
  {
    value: 'updatedAt',
    label: 'Оновлено',
    display: true,
    width: 140,
    minWidth: 120,
  },
];

type PartnerRow = {
  id: string;
  name: string;
  nameEn: string;
  nameDe: string;
  link: string;
  image?: string | null;
  description: string;
  descriptionEn: string;
  descriptionDe: string;
  descriptionLong: string;
  descriptionLongEn: string;
  descriptionLongDe: string;
  annotation: string;
  annotationEn: string;
  annotationDe: string;
  createdAt: number;
  updatedAt: number;
};

export default function Partners() {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [name, setName] = React.useState('');

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'partners:tableColumns'
  );

  const [updatePartnerImageObj, setUpdatePartnerImageObj] =
    React.useState<null | { id?: string; image?: string }>(null);
  const handleUpdateImageModalClose = () => {
    setUpdatePartnerImageObj(null);
    list.reload();
  };
  const [modalPartnerId, setModalPartnerId] = React.useState<string>('');
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);
  const handleModalClose = () => {
    setModalPartnerId('');
    setModalPartnerName('');
    setModalPartnerNameEn('');
    setModalPartnerNameDe('');
  };
  const [modalPartnerName, setModalPartnerName] = React.useState<string>('');
  const [modalPartnerNameEn, setModalPartnerNameEn] =
    React.useState<string>('');
  const [modalPartnerNameDe, setModalPartnerNameDe] =
    React.useState<string>('');
  const [modalPartnerDescription, setModalPartnerDescription] =
    React.useState<string>('');
  const [modalPartnerDescriptionEn, setModalPartnerDescriptionEn] =
    React.useState<string>('');
  const [modalPartnerDescriptionDe, setModalPartnerDescriptionDe] =
    React.useState<string>('');
  const [modalPartnerDescriptionLong, setModalPartnerDescriptionLong] =
    React.useState<string>('');
  const [modalPartnerDescriptionLongEn, setModalPartnerDescriptionLongEn] =
    React.useState<string>('');
  const [modalPartnerDescriptionLongDe, setModalPartnerDescriptionLongDe] =
    React.useState<string>('');
  const [modalPartnerAnnotation, setModalPartnerAnnotation] =
    React.useState<string>('');
  const [modalPartnerAnnotationEn, setModalPartnerAnnotationEn] =
    React.useState<string>('');
  const [modalPartnerAnnotationDe, setModalPartnerAnnotationDe] =
    React.useState<string>('');
  const [modalPartnerLink, setModalPartnerLink] = React.useState<string>('');
  const {
    errorSummary: updateErrors,
    setApiError: setUpdateError,
    clearErrors: clearUpdateErrors,
  } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити партнера. Спробуйте пізніше',
    resetOn: [modalPartnerId],
  });

  const list = useResourceList<PartnerRow>({
    fetch: ({ page, perPage }) =>
      partnersApi.list(page, perPage, name || undefined),
    filters: [name],
  });

  const updatePartner = async () => {
    if (!modalPartnerId) {
      return;
    }

    clearUpdateErrors();
    try {
      await partnersApi.update(
        modalPartnerId,
        modalPartnerName,
        modalPartnerNameEn,
        modalPartnerNameDe,
        modalPartnerDescription,
        modalPartnerDescriptionEn,
        modalPartnerDescriptionDe,
        modalPartnerDescriptionLong,
        modalPartnerDescriptionLongEn,
        modalPartnerDescriptionLongDe,
        modalPartnerAnnotation,
        modalPartnerAnnotationEn,
        modalPartnerAnnotationDe,
        modalPartnerLink
      );
      handleModalClose();
      list.reload();
    } catch (err: any) {
      console.error(err);
      const errorMessage = setUpdateError(err)[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    }
  };

  const setRowDataEdition = (rowData: any) => {
    setModalPartnerId(rowData.id);
    setModalPartnerName(rowData.name);
    setModalPartnerNameEn(rowData.nameEn);
    setModalPartnerNameDe(rowData.nameDe);
    setModalPartnerLink(rowData.link);
    setModalPartnerDescription(rowData.description);
    setModalPartnerDescriptionEn(rowData.descriptionEn);
    setModalPartnerDescriptionDe(rowData.descriptionDe);
    setModalPartnerDescriptionLong(rowData.descriptionLong);
    setModalPartnerDescriptionLongEn(rowData.descriptionLongEn);
    setModalPartnerDescriptionLongDe(rowData.descriptionLongDe);
    setModalPartnerAnnotation(rowData.annotation);
    setModalPartnerAnnotationEn(rowData.annotationEn);
    setModalPartnerAnnotationDe(rowData.annotationDe);
  };

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  const renderColumn = (column: (typeof TABLE_COLUMNS)[0]) => {
    if (column.display === false) {
      return null;
    }

    const commonProps = {
      key: column.value,
      width: column.width,
      minWidth: column.minWidth || 80,
      fixed:
        column.value === firstVisibleColumnValue
          ? ('left' as const)
          : undefined,
    };

    switch (column.value) {
      case 'name':
        return (
          <Column {...commonProps}>
            <HeaderCell>Назва</HeaderCell>
            <Cell dataKey="name" />
          </Column>
        );
      case 'link':
        return (
          <Column {...commonProps}>
            <HeaderCell>Посилання</HeaderCell>
            <Cell
              className="table-cell-link"
              dataKey="link"
              renderCell={(link: string) => (
                <a
                  className="table-link"
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  title={link}
                >
                  {link}
                </a>
              )}
            />
          </Column>
        );
      case 'image':
        return (
          <Column {...commonProps}>
            <HeaderCell>Зображення</HeaderCell>
            <Cell>
              {rowData => (
                <div
                  onClick={() =>
                    setUpdatePartnerImageObj({
                      id: rowData.id,
                      image: rowData.image,
                    })
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <ImageThumb src={rowData.image} alt={rowData.name} />
                </div>
              )}
            </Cell>
          </Column>
        );
      case 'description':
        return (
          <Column {...commonProps}>
            <HeaderCell>Опис</HeaderCell>
            <Cell className="table-cell-description">
              {rowData => {
                return (
                  <div
                    style={{
                      minHeight: 80,
                      maxHeight: 140,
                      overflow: 'auto',
                    }}
                  >
                    {rowData.description}
                  </div>
                );
              }}
            </Cell>
          </Column>
        );
      case 'descriptionLong':
        return (
          <Column {...commonProps}>
            <HeaderCell>Довгий опис</HeaderCell>
            <Cell className="table-cell-description">
              {rowData =>
                truncatedTextCell(rowData.descriptionLong, {
                  maxLength: PARTNER_LONG_DESCRIPTION_PREVIEW_MAX_LENGTH,
                  stripTags: true,
                  className: 'partners-table__description-preview',
                  forceTooltip: true,
                })
              }
            </Cell>
          </Column>
        );
      case 'createdAt':
        return (
          <Column {...commonProps}>
            <HeaderCell>Створено</HeaderCell>
            <Cell
              dataKey="createdAt"
              renderCell={(content: number) => formatDate(String(content))}
            />
          </Column>
        );
      case 'updatedAt':
        return (
          <Column {...commonProps}>
            <HeaderCell>Оновлено</HeaderCell>
            <Cell
              dataKey="updatedAt"
              renderCell={(content: number) => formatDate(String(content))}
            />
          </Column>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Партнери"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за назвою"
              value={name || ''}
              onChange={setName}
            />
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
            <Button
              appearance="primary"
              onClick={() => setCreateModalOpen(true)}
            >
              Створити
            </Button>
          </>
        }
      >
        <div className="section-card">
          <Table
            className="requests-table"
            style={{ width: '100%' }}
            wordWrap="keep-all"
            loading={list.loading}
            autoHeight={true}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            data={list.data}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowHeight={140}
            rowClassName={getRowClassName}
          >
            {renderRowNumberColumn({ page: list.page, perPage: list.perPage })}

            {tableColumns.map(renderColumn)}

            <Column fixed="right" width={100}>
              <HeaderCell>Дії</HeaderCell>

              <Cell style={{ padding: '4px 6px' }}>
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Редагувати</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => {
                          setRowDataEdition(rowData);
                        }}
                      >
                        <EditIcon />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити партнера ${rowData.name}?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          partnersApi
                            .remove(rowData.id)
                            .catch(console.error)
                            .then(() => list.reload());
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={list.totalAmount}
            activePage={list.page}
            limit={list.perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={list.setPerPage}
            onChangePage={list.goToPage}
          />
        </div>

        <EditPartnerModal
          modalPartnerId={modalPartnerId}
          modalPartnerName={modalPartnerName}
          modalPartnerNameEn={modalPartnerNameEn}
          modalPartnerNameDe={modalPartnerNameDe}
          modalPartnerDescription={modalPartnerDescription}
          modalPartnerDescriptionEn={modalPartnerDescriptionEn}
          modalPartnerDescriptionDe={modalPartnerDescriptionDe}
          modalPartnerDescriptionLong={modalPartnerDescriptionLong}
          modalPartnerDescriptionLongEn={modalPartnerDescriptionLongEn}
          modalPartnerDescriptionLongDe={modalPartnerDescriptionLongDe}
          modalPartnerAnnotation={modalPartnerAnnotation}
          modalPartnerAnnotationEn={modalPartnerAnnotationEn}
          modalPartnerAnnotationDe={modalPartnerAnnotationDe}
          modalPartnerLink={modalPartnerLink}
          handleModalClose={handleModalClose}
          setModalPartnerName={setModalPartnerName}
          setModalPartnerNameEn={setModalPartnerNameEn}
          setModalPartnerNameDe={setModalPartnerNameDe}
          setModalPartnerDescription={setModalPartnerDescription}
          setModalPartnerDescriptionEn={setModalPartnerDescriptionEn}
          setModalPartnerDescriptionDe={setModalPartnerDescriptionDe}
          setModalPartnerDescriptionLong={setModalPartnerDescriptionLong}
          setModalPartnerDescriptionLongEn={setModalPartnerDescriptionLongEn}
          setModalPartnerDescriptionLongDe={setModalPartnerDescriptionLongDe}
          setModalPartnerAnnotation={setModalPartnerAnnotation}
          setModalPartnerAnnotationEn={setModalPartnerAnnotationEn}
          setModalPartnerAnnotationDe={setModalPartnerAnnotationDe}
          setModalPartnerLink={setModalPartnerLink}
          submit={updatePartner}
          errorSummary={updateErrors}
        />

        <CreatePartnerModal
          open={createModalOpen}
          setOpen={setCreateModalOpen}
          callback={() => {
            list.reload();
          }}
        />

        <UploadImageModal
          title="Редагувати зображення партнера"
          actionUrl={`${API_DOMAIN}/api/partners/${updatePartnerImageObj?.id}/image`}
          object={updatePartnerImageObj}
          handleModalClose={handleUpdateImageModalClose}
        />
      </PageLayout>
    </>
  );
}
